import React, { useEffect, useRef } from 'react';
import iconePinky from '../assets/icone_pinky.png';
import * as THREE from 'three';
import CLOUDS from 'vanta/dist/vanta.clouds.min';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
// SimpleWave supprimé pour un fond de nuages animés plein écran
import './Hero.css';
import './HeroMain.css';
import './ParticlesFix.css';

gsap.registerPlugin(ScrollTrigger);

const Hero = () => {
  const heroRef = useRef(null);
  const vantaRef = useRef(null);
  const vantaInstanceRef = useRef(null);
  const logoRef = useRef(null);
  const hoursRef = useRef(null);
  // Refs supprimées pour pearls et drinks




  useEffect(() => {
    // Initialisation Vanta CLOUDS (derrière tout)
    if (vantaRef.current && !vantaInstanceRef.current) {
      vantaInstanceRef.current = CLOUDS({
        el: vantaRef.current,
        THREE,
        mouseControls: false,
        touchControls: false,
        gyroControls: false,
        minHeight: 200.0,
        minWidth: 200.0,
        skyColor: 0xFFE1EE,   // rose très pâle
        cloudColor: 0xFFB3D3, // nuages rosés
        // sunColor supprimé pour enlever les effets de rayons
        speed: 0.35,
      });
    }

    // titleRef supprimé car plus utilisé
    const logo = logoRef.current;
    const hours = hoursRef.current;

    const heroElement = heroRef.current; // Capturer pour le cleanup

    // Vérifier que les éléments principaux existent avant d'animer
    if (!logo || !hours) return;

    // Flag pour vérifier si le composant est toujours monté
    let isMounted = true;

    // Stocker les animations et event listeners pour le nettoyage
    const animations = [];
    const eventListeners = [];

    // Initialiser les états de départ des éléments
    gsap.set(hours, { opacity: 0, y: 40 });
    gsap.set(logo, { y: 50, scale: 0.8 }); // Pas d'opacité pour préserver les styles CSS

    // Animation d'entrée séquentielle élégante
    const tl = gsap.timeline({ 
      delay: 0.3,
      onComplete: () => {
        if (!isMounted) return; // Ne pas continuer si le composant est démonté
      }
    });
    
    tl.to(logo, {
      duration: 1.0,
      y: 0,
      scale: 1,
      ease: "back.out(1.3)" // Effet bouncy pour l'apparition
    }, "-=0.5")
    .to(hours, {
      duration: 0.8,
      y: 0,
      opacity: 1,
      ease: "power2.out"
    }, "-=0.4");

    animations.push(tl);

    // ✨ ANIMATIONS DE SCROLL OPTIMISÉES
    
    // 1. Parallaxe subtil du logo au scroll
    const logoParallax = gsap.to(logo, {
      yPercent: -30,
      ease: "none",
      scrollTrigger: {
        trigger: heroRef.current,
        start: "top bottom",
        end: "bottom top",
        scrub: 1
      }
    });
    animations.push(logoParallax);

    // 2. Effet de "breathing" pour le logo
    const logoElement = logo.querySelector('h1');
    if (logoElement) {
      const breathingAnim = gsap.to(logoElement, {
        scale: 1.02,
        duration: 4,
        ease: "power2.inOut",
        repeat: -1,
        yoyo: true
      });
      animations.push(breathingAnim);
    }

    // 3. Text reveal effect supprimé car plus de slogan

    // 4. Animation du titre avec l'effet nuage (remplace l'image bubble tea)

    // 6. Parallax des éléments internes (CORRIGÉ - pas du background)
    const containerParallax = gsap.to(heroRef.current.querySelector('div'), {
      yPercent: -10, // Effet parallax subtil sur le container
      ease: "none",
      scrollTrigger: {
        trigger: heroRef.current,
        start: "top bottom",
        end: "bottom top",
        scrub: 1
      }
    });
    animations.push(containerParallax);

    // 7. Animation des cartes d'heures COMPLÈTE avec flottement
    const hourCards = hours.querySelectorAll('[data-hour-card]');
    hourCards.forEach((card, index) => {
      // Animation d'apparition en stagger COMPLÈTE
      const cardReveal = gsap.fromTo(card, 
        { 
          y: 60,
          opacity: 0,
                      scale: 0.95,
            rotationY: 10 
        },
        {
          y: 0,
          opacity: 1,
          scale: 1,
                      rotationY: 0, 
          duration: 0.8,
          ease: "power2.out",
          delay: index * 0.1,
          scrollTrigger: {
            trigger: card,
                          start: "top 90%",
              end: "top 60%", 
            toggleActions: "play none none reverse"
          }
        }
      );
      animations.push(cardReveal);

      // Effet de flottement subtil au scroll (REMIS !)
      const cardFloat = gsap.to(card, {
                  y: -8,
          rotationX: 2,
          transformPerspective: 1000, 
        ease: "none",
        scrollTrigger: {
          trigger: card,
                      start: "top 80%",
            end: "bottom 20%",
            scrub: 1.5 
        }
      });
      animations.push(cardFloat);

      // Hover handlers AMÉLIORÉS
      const mouseEnterHandler = () => {
        gsap.to(card, {
          scale: 1.02,
          y: -3,
          boxShadow: '0 15px 30px rgba(0, 0, 0, 0.15)',
          duration: 0.3,
          ease: "power2.out"
        });
      };

      const mouseLeaveHandler = () => {
        gsap.to(card, {
          scale: 1,
          y: 0,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          duration: 0.3,
          ease: "power2.out"
        });
      };

      card.addEventListener('mouseenter', mouseEnterHandler);
      card.addEventListener('mouseleave', mouseLeaveHandler);
      
      eventListeners.push(
        { element: card, event: 'mouseenter', handler: mouseEnterHandler },
        { element: card, event: 'mouseleave', handler: mouseLeaveHandler }
      );
    });

    // Section bubble tea supprimée - garde seulement les nuages
    const createOptimizedBubbles = () => {
      const bubbles = [];
      
      // Plus de bubble tea ni perles - seulement nuages Vanta
      const bubbleCount = 0;
      
      for (let i = 0; i < bubbleCount; i++) {
          const bubble = document.createElement('div');
          bubble.className = 'floating-bubble';
          // bubble.innerHTML = '🧋'; // Bubble tea supprimé
          // 🔧 Fix: Assurer que les dimensions du conteneur sont valides
          const containerWidth = heroRef.current.offsetWidth || window.innerWidth;
          const containerHeight = heroRef.current.offsetHeight || window.innerHeight;
          
          bubble.style.cssText = `
            position: absolute;
            font-size: ${Math.random() * 25 + 15}px;
            pointer-events: none;
            z-index: 1;
            left: ${Math.max(20, Math.min(Math.random() * (containerWidth - 80) + 40, containerWidth - 60))}px;
            top: ${Math.max(80, Math.min(Math.random() * (containerHeight * 0.7) + 120, containerHeight - 100))}px;
            opacity: 0.5;
            filter: drop-shadow(0 2px 4px rgba(255, 105, 180, 0.3));
            will-change: transform;
          `;
          heroRef.current.appendChild(bubble);
          bubbles.push(bubble);

        // ⚡ Optimized floating animation - 40% shorter duration
        const floatAnim = gsap.to(bubble, {
          y: -20, // Less movement
          x: Math.random() * 30 - 15, // Reduced range
          rotation: 180, // Less rotation
          duration: 5 + Math.random() * 2, // Faster
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
          force3D: true // GPU acceleration
        });
        animations.push(floatAnim);

        // ⚡ Simplified scroll animation - only if not mobile
        const isMobile = window.innerWidth <= 768;
        if (!isMobile) {
          const bubbleScroll = gsap.to(bubble, {
            y: -50, // Less movement
            opacity: 0.9,
            scale: 1.1,
            ease: "none",
            scrollTrigger: {
              trigger: heroRef.current,
              start: "top bottom",
              end: "bottom top",
              scrub: 1 // Faster response
            }
          });
          animations.push(bubbleScroll);
        }
      }

      // Plus de particules drink - supprimé
      const drinkCount = 0;
      for (let i = 0; i < drinkCount; i++) {
        const particle = document.createElement('div');
        // particle.innerHTML = '🧋'; // Particle supprimée
        // 🔧 Fix: Assurer des positions valides
        const containerWidth = heroRef.current.offsetWidth || window.innerWidth;
        const containerHeight = heroRef.current.offsetHeight || window.innerHeight;
        const SAFE = 36;
        const usableWidth = Math.max(0, containerWidth - SAFE * 2);
        const colWidth = usableWidth / drinkCount;
        const baseX = SAFE + (i + 0.5) * colWidth;
        const jitterX = (Math.random() - 0.5) * Math.min(24, colWidth * 0.35);
        const finalX = Math.max(SAFE, Math.min(containerWidth - SAFE, baseX + jitterX));
        const startTop = Math.max(60, Math.min(Math.random() * (containerHeight * 0.7) + 80, containerHeight - 120));

        particle.style.cssText = `
          position: absolute;
          font-size: ${Math.random() * 14 + 14}px;
          pointer-events: none;
          z-index: 0;
          left: ${finalX}px;
          top: ${startTop}px;
          opacity: ${Math.random() * 0.2 + 0.3};
          filter: drop-shadow(0 1px 3px rgba(214,75,120,0.15));
          will-change: transform;
        `;
        heroRef.current.appendChild(particle);
        bubbles.push(particle);

        // ⚡ Faster organic animation
        const particleAnim = gsap.to(particle, {
          y: -26,
          x: Math.sin(i) * 20,
          rotation: Math.random() * 6,
          duration: 10 + Math.random() * 4,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
          force3D: true
        });
        animations.push(particleAnim);

        // ⚡ Scroll animation only on desktop
        const isMobileParticle = window.innerWidth <= 768;
        if (!isMobileParticle) {
          const particleScroll = gsap.to(particle, {
            y: -90,
            opacity: 0.5,
            scale: 1.05,
            ease: "none",
            scrollTrigger: {
              trigger: heroRef.current,
              start: "top bottom",
              end: "bottom top",
              scrub: 2 // Faster
            }
          });
          animations.push(particleScroll);
        }
      }

      return bubbles;
    };

    const bubbles = createOptimizedBubbles();

            // 9. Animation du logo au scroll (REMISE !)
        const logoScroll = gsap.to(logo, {
          yPercent: -30,
          opacity: 0.8,
          ease: "none",
          scrollTrigger: {
            trigger: heroRef.current,
            start: "top top",
            end: "center top",
            scrub: 1
          }
        });
        animations.push(logoScroll);

    // Perles supprimées - garde seulement les nuages
    const pearls = null; // pearlsRef supprimé
    if (pearls) {
      // 🔋 Reduce pearls on mobile - refs supprimées
      const pearlsContainer = null; // pearlsRef supprimé
      const screenWidth = window.innerWidth;
      const containerWidth = pearlsContainer?.clientWidth || screenWidth;
      const containerHeight = pearlsContainer?.clientHeight || window.innerHeight;
      const isMobile = containerWidth <= 768;
      const isSmallMobile = containerWidth <= 480;
      const isTinyScreen = containerWidth <= 360;
      
      // Nombre de perles; un peu plus en desktop
      const maxPearls = isMobile ? 7 : 12;
      const activePearls = Array.from(pearls).slice(0, maxPearls);
      
      activePearls.forEach((pearl, i) => {
        
        // Adaptation de l'échelle selon l'écran
        let pearlScale = 1;
        if (isTinyScreen) {
          pearlScale = 0.9; // maintain visibility on very small screens
        } else if (isSmallMobile) {
          pearlScale = 0.95;
        } else if (isMobile) {
          pearlScale = 1.0;
        }
        
        // ⚡ Optimized distribution with safe margins from edges
        const totalPearls = activePearls.length;
        const SAFE = 28; // Keep pearls away from edges
        const usableWidth = Math.max(0, containerWidth - SAFE * 2);
        const segmentWidth = usableWidth / totalPearls;
        // Répartition alternée pour croiser avec les bubble tea
        const altOffsetPearl = ((i + 1) % 2) * (segmentWidth / 2);
        const baseX = SAFE + (i + 0.5) * segmentWidth + altOffsetPearl;
        const randomVariation = (Math.random() - 0.5) * Math.min(20, segmentWidth * 0.25);
        const finalX = Math.max(SAFE, Math.min(containerWidth - SAFE, baseX + randomVariation));
        
        // Nettoyer toute animation résiduelle et forcer un état propre
        gsap.killTweensOf(pearl);
        // Point de départ aléatoire au-dessus du viewport pour éviter l'effet de "saut"
        const startY = -Math.random() * (containerHeight * 0.9) - 40;
        gsap.set(pearl, {
          x: 0,
          y: startY,
          left: finalX,
          top: 0,
          scale: pearlScale,
          force3D: true // GPU acceleration
        });

        // ⚡ Consistent fall top -> bottom (no bounce, no reverse)
        const fallAnim = gsap.timeline({ repeat: -1, defaults: { ease: 'none' } });
        fallAnim
          .set(pearl, { y: startY })
          .to(pearl, {
            y: containerHeight + 60,
            duration: 2.6 + Math.random() * 1.2
          })
          .add(() => {
            // Reset position dans la même colonne avec léger jitter borné
            const jitter = (Math.random() - 0.5) * Math.min(20, segmentWidth * 0.25);
            const newX = Math.max(SAFE, Math.min(containerWidth - SAFE, baseX + jitter));
            const resetStartY = -Math.random() * (containerHeight * 0.9) - 40;
            gsap.set(pearl, { left: newX, x: 0, y: resetStartY });
          });

        // Dérive horizontale permanente pour éviter toute impression de stagnation
        const pearlDrift = gsap.to(pearl, {
          x: "+=8",
          duration: 1.8 + Math.random() * 1.2,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut"
        });

        // Rotation désactivée pour un mouvement purement vertical
        // rotationAnim supprimé car non utilisé

        // ⚡ Simplified wind effect - only on desktop, clamp to safe bounds
        // Vent désactivé: on conserve un déplacement uniquement vertical
        animations.push(fallAnim);
        animations.push(pearlDrift);
      });
    }

    // Drinks qui tombent supprimés - garde seulement les nuages
    const drinks = null; // drinksRef supprimé
    if (drinks) {
      // Refs supprimées
      const containerWidth = window.innerWidth;
      const containerHeight = window.innerHeight;
      const isMobileNarrow = containerWidth <= 768;
      const total = drinks.length;
      const SAFE = 24;
      const usableWidth = Math.max(0, containerWidth - SAFE * 2);
      const segmentWidth = usableWidth / total;

      Array.from(drinks).forEach((drink, i) => {
        // Décalage alterné pour une meilleure répartition
        const altOffset = (i % 2) * (segmentWidth / 2);
        const baseX = SAFE + (i + 0.5) * segmentWidth + altOffset;
        const jitter = (Math.random() - 0.5) * Math.min(22, segmentWidth * 0.25);
        const finalX = Math.max(SAFE, Math.min(containerWidth - SAFE, baseX + jitter));

        gsap.killTweensOf(drink);
        const startYDrink = -140 - Math.random() * (containerHeight * 0.6);
        gsap.set(drink, {
          left: finalX,
          x: 0,
          y: startYDrink,
          opacity: isMobileNarrow ? 0.65 : 0.8,
          scale: isMobileNarrow ? 0.9 : 1,
          force3D: true
        });

        const fallDrink = gsap.timeline({ repeat: -1 });
        fallDrink
          .set(drink, { y: startYDrink })
          .to(drink, {
            y: containerHeight + 120,
            duration: 8 + Math.random() * 3,
            ease: "none"
          })
          .add(() => {
            const jitter2 = (Math.random() - 0.5) * Math.min(22, segmentWidth * 0.25);
            const newX = Math.max(SAFE, Math.min(containerWidth - SAFE, baseX + jitter2));
            const resetStartYDrink = -140 - Math.random() * (containerHeight * 0.6);
            gsap.set(drink, { left: newX, x: 0, y: resetStartYDrink });
          });

        // Légère oscillation horizontale pour éviter la stagnation
        const drinkDrift = gsap.to(drink, {
          x: "+=10",
          duration: 2 + Math.random() * 1.2,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut"
        });

        animations.push(fallDrink);
        animations.push(drinkDrift);
      });
    }

    return () => {
      // Destroy Vanta instance proprement
      if (vantaInstanceRef.current && typeof vantaInstanceRef.current.destroy === 'function') {
        vantaInstanceRef.current.destroy();
        vantaInstanceRef.current = null;
      }
      // Marquer le composant comme démonté
      isMounted = false;
      
      // Nettoyer toutes les animations
      animations.forEach(anim => {
        if (anim && typeof anim.kill === 'function') {
          anim.kill();
        }
      });
      
      // Nettoyer SEULEMENT les ScrollTriggers de ce composant
      const heroTriggers = ScrollTrigger.getAll().filter(trigger => 
        trigger.trigger === heroElement || 
        (heroElement && heroElement.contains(trigger.trigger))
      );
      heroTriggers.forEach(trigger => trigger.kill());
      
      // Nettoyer les event listeners
      eventListeners.forEach(({ element, event, handler }) => {
        if (element && handler && element.removeEventListener) {
          element.removeEventListener(event, handler);
        }
      });
      
      // Nettoyer les bulles
      bubbles.forEach(bubble => {
        if (bubble && bubble.parentNode) {
          bubble.parentNode.removeChild(bubble);
        }
      });
      
      // Cleanup text reveal supprimé car plus de slogan
    };
  }, []);

  // Force refresh des animations au montage
  useEffect(() => {
    const timer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

            // ✅ STYLES DÉPLACÉS VERS CSS - Plus de styles inline!

  return (
    <>
      {/* Partie principale Hero - Style Krem & Kanel avec vague intégrée */}
      <section className="hero-main" ref={heroRef}>
        {/* Vanta.js Clouds - couche la plus basse */}
        <div className="vanta-clouds" ref={vantaRef} aria-hidden="true" />

        <div className="hero-container-main">
          
          {/* Header supprimé - plus de coordonnées */}

          {/* Logo central */}
          <div className="hero-logo-section-main" ref={logoRef}>
            <h1 className="hero-main-logo-style" aria-label="Pinky Cloud">
              <img src={iconePinky} alt="Pinky Cloud" className="hero-logo-image" />
            </h1>
          </div>

          {/* Image du produit supprimée - remplacée par l'effet nuage */}
        </div>

        {/* Perles et bubble tea supprimés - garde seulement les nuages Vanta */}

        {/* Vague supprimée pour un fond de nuages animé */}
      </section>



      {/* Zone séparée pour les heures d'ouverture style Krem & Kanel */}
      <section className="hours-container-main">
        <div className="hours-section-main" ref={hoursRef}>
          <h2 className="hours-title-main">HEURES D'OUVERTURE</h2>
          
          <div className="hours-cards-main">
            <div
              className="hour-card-base hour-card-pink"
              data-hour-card="1"
            >
              <div className="day-name-main">Lun</div>
              <div className="hour-status-main">FERMÉ</div>
            </div>

            <div
              className="hour-card-base hour-card-pink"
              data-hour-card="2"
            >
              <div className="day-name-main">Mar — Jeu</div>
              <div className="hour-time-main">10:00 À 02:00</div>
            </div>

            <div
              className="hour-card-base hour-card-pink"
              data-hour-card="3"
            >
              <div className="day-name-main">Ven</div>
              <div className="hour-time-main">18:00 À 02:00</div>
            </div>

            <div
              className="hour-card-base hour-card-pink"
              data-hour-card="4"
            >
              <div className="day-name-main">Sam — Dim</div>
              <div className="hour-time-main">10:00 À 02:00</div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero; 